import React, { useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { ReduxUpdater } from "../../../redux/ReduxUpdater";
import { useSelector } from "react-redux";
import { documentLoadingErrorState, documentLoadingState, documentPageState } from "../../../redux/slices/statusesSlice";

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfWithSpinnerProps {
  src: string;
  alt: string;
  rotation: number;
  className?: string;          // extra classes for <img>
  wrapperClassName?: string;   // extra classes for wrapper
}

export const PdfWithSpinner: React.FC<PdfWithSpinnerProps> = ({
  src,
  alt,
  rotation = 0,
  className = "",
  wrapperClassName = "",
}) => {
    const currentPdfLoadingState = useSelector(documentLoadingState);
    const currentPdfLoadingErrorState = useSelector(documentLoadingErrorState);
    const pageNumber = useSelector(documentPageState);

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        ReduxUpdater.documentLoading = false;
        ReduxUpdater.documentPages = numPages;
    }

    const options = {
        cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
        wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
    };

    // Use useLayoutEffect to measure the container width after DOM updates
    React.useLayoutEffect(() => {
        if (containerRef.current) {
        // Set the container width, potentially adding some margin/padding logic
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, []);

    return (
        <div
            ref={containerRef}
            className={`relative inline-block overflow-auto ${wrapperClassName}`}
        >
            {/* PDF (hidden until fully loaded) */}
            {!currentPdfLoadingErrorState && (
                <Document
                    file={src}
                    onLoadSuccess={onDocumentLoadSuccess}
                    options={options}
                    // scale={1.65}
                >
                    <Page
                        pageNumber={pageNumber}
                        height={500}
                        width={containerWidth - 8}
                        className='m-auto w-[fit-content]'
                    />
                </Document>
            )}

            {/* Spinner overlay while loading */}
            {currentPdfLoadingState && !currentPdfLoadingErrorState && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                    {/* Preline/Tailwind spinner */}
                    <div
                        className="inline-flex h-10 w-10 rounded-full border-2 border-current border-r-transparent animate-spin
                                align-[-0.125em] text-blue-400"
                        role="status"
                        aria-label="Loading"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}

            {/* Simple error state */}
            {currentPdfLoadingErrorState && (
                <div className="flex h-full min-h-[120px] items-center justify-center bg-gray-100 text-sm text-gray-600">
                    Failed to load PDF
                </div>
            )}
        </div>
    );
};
